import React, { useState, useEffect, useRef, useContext } from 'react';
import { actions } from '../../../state/actions';
import { StateContext } from '../../../state';
import { logger } from '../../../utils/logging';
import { ERROR_TYPES } from '../../../utils/enums';
import { LOGGING } from '../../../utils/enums';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';
import clsx from 'clsx';
import poweredByGoogleLogo from './powered_by_google_on_white_hdpi.png';
import { BROWSER_STORAGE_ITEMS } from '../../../utils/enums';
import {
  mapStateNameToAbbreviation,
  stateNames,
  mapStateAbbreviationToName,
} from './data';

export const initialState = {
  street_address_1: '',
  street_address_2: '',
  city: '',
  state: '',
  postal_code: '',
  country: '',
  google_place_raw: null,
};

const initialPlaceState = {
  state: '',
  country: '',
  city: '',
  postal_code: '',
  street_number: '',
  route: '',
};

const parsePlace = (place) => {
  // google places api uses several different types to identify cities so we need to check for any that match these
  const cityTypes = [
    'locality',
    'sublocality',
    'sublocality_level_1',
    'sublocality_level_2',
    'sublocality_level_3',
    'sublocality_level_4',
  ];

  // convert place object to address fields
  const fields = {};
  let cityCommponentFound = false;

  place.address_components.forEach((component) => {
    // if (cityTypes.includes(component.types[0])) {
    
    let isCityComponent = false;

    if (!cityCommponentFound) {
      for (let i = 0; i < component.types.length; i++) {
        if (cityTypes.includes(component.types[i])) {
          isCityComponent = true;
          cityCommponentFound = true;
          fields.locality = component.long_name;
          break;
        }
      }
    }

    if (!isCityComponent) {
      // use short_name for state in order to get state abbreviation; otherwise use long_name for all other address components
      fields[component.types[0]] =
        component[
          (component.types[0] === 'administrative_area_level_1' || component.types[0] === 'route')
            ? 'short_name'
            : 'long_name'
        ];
    }
  });

  // get full zip code with suffix
  if (fields.postal_code_suffix)
    fields.postal_code += `-${fields.postal_code_suffix}`;

  // include google-provided formatted address to the fields object
  fields.formatted_address = place.formatted_address;

  return fields;
};

// Styles
const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: 300,
    alignItems: 'center',
  },
  input: {
    width: 300,
    '& + &': {
      marginTop: 20,
    },
  },
  inline: {
    width: 'unset',
  },
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),
  },
  hidden: {
    display: 'none',
  },
  primary: {
    fontSize: 16,
  },
  secondary: {
    fontSize: 14,
  },
  disabled: {
    pointerEvents: 'none',
    background: '#ECECEC',
  },
  inputLabel: {
    '&.outlined.marginDense:not(.shrink)': {
      transform: 'translate(8px, 12px) scale(1)',
    },
  },
  minimalPadding: {
    padding: 0,
  },
  opaque: {
    opacity: '1 !important',
  },
}));

function loadGooglePlacesScript(url, onload) {
  const script = document.createElement('script');
  script.src = url;
  script.id = 'google-places-autocomplete-api-script';
  script.type = 'text/javascript';
  script.async = true;
  script.onload = onload;
  document.getElementsByTagName('head')[0].appendChild(script);

  // return a function to remove the script and cleanup Google-created elements
  return () => {
    // remove the script
    const scriptElement = document.getElementById(
      'google-places-autocomplete-api-script'
    );
    if (scriptElement) {
      scriptElement.remove();
    }

    // clean up google maps internal elements
    const cleanup = [
      'gmp-internal-element-support-verification',
      'google-maps-api-libraries',
      'google-maps-api-resolvers',
      'google-maps-api',
      'gmp-internal-dialog',
      'gmp-internal-camera-control',
    ];

    cleanup.forEach((id) => {
      const elementById = document.getElementById(id);
      if (elementById) {
        elementById.remove();
      }
    });

    // remove any google maps related scripts from head
    document.head.querySelectorAll('script').forEach((script) => {
      if (script.src.includes('maps.googleapis.com')) {
        script.remove();
      }
    });

    // reset the services
    autocompleteService.current = null;
    placesService.current = null;
    googleSessionToken = null;
  };
}

// initialize google maps api services
const autocompleteService = { current: null };
const placesService = { current: null };
let googleSessionToken = null;

function onGooglePlacesScriptLoad() {
  placesService.current = new window.google.maps.places.PlacesService(
    document.createElement('div')
  );
  autocompleteService.current =
    new window.google.maps.places.AutocompleteService();
  googleSessionToken = new window.google.maps.places.AutocompleteSessionToken();
}
export default function CollectAddressGooglePlacesAPIEmbeddable({
  debug,
  googleApiKey,
  restrictToGoogleResults,
  allowManualOverride,
  autocompleteThrottleDurationInMs = 200,
  values = initialState,
  onChange,
  onValidationChange,
  enableBrowserCache = false,
  browserCacheKey = BROWSER_STORAGE_ITEMS.ADDRESS_FORM_VALUES,
}) {
  // if restrictToGoogleResults is true and allowManualOverride is false, user will only be able to select a google result (city, state, postal code fields will be disabled)
  // if restrictToGoogleResults is true and allowManualOverride is true, user will have option to disable restrictToGoogleResults and make changes to the address manually (changes will be tracked as manual edits)
  // if restrictToGoogleResults is false, user can either select a google result or enter the address manually (changes will still be tracked as manual edits, if the entered fields do not match a google result)
  // note: manual_edits is a naive comparison and just looks to see if the field values differ from the google result

  const classes = useStyles();

  // State variables
  const [autocompleteComponentValue, setAutocompleteComponentValue] = useState(
    initialState.street_address_1 || null
  ); // initialize autocomplete component value to null if field is initially empty (for non-cached form)
  const [addressSearchInputValue, setAddressSearchInputValue] = useState(
    initialState.street_address_1
  ); // search input field value
  const [googlePlacesOptionsForDropdown, setGooglePlacesOptionsForDropdown] =
    useState([]); // google places results for dropdown
  const [googlePlaceRaw, setGooglePlaceRaw] = useState(
    initialState.google_place_raw
  ); // currently selected raw google place object
  const [limitToGoogleResults, setLimitToGoogleResults] = useState(
    restrictToGoogleResults
  ); // local state can be toggled depending on value of allowManualOverride flag
  const [error, setError] = useState(false);
  const [addressLine2, setAddressLine2] = useState(
    initialState.street_address_2
  );
  const [city, setCity] = useState(initialState.city);
  const [postalCode, setPostalCode] = useState(initialState.postal_code);
  const [stateDropdownValue, setStateDropdownValue] = useState(
    initialState.state
  );

  // on component mount, initialize form with cached values from session storage
  useEffect(() => {
    let cachedValues;
    if (enableBrowserCache) {
      cachedValues = JSON.parse(window.sessionStorage.getItem(browserCacheKey));
    }
    if (cachedValues) {
      setAutocompleteComponentValue(cachedValues.street_address_1);
      setAddressSearchInputValue(cachedValues.street_address_1);
      setGooglePlaceRaw(cachedValues.google_place_raw);
      setAddressLine2(cachedValues.street_address_2);
      setCity(cachedValues.city);
      setPostalCode(cachedValues.postal_code);
      setStateDropdownValue(mapStateAbbreviationToName[cachedValues.state]);
    }
  }, [enableBrowserCache, browserCacheKey]);

  // Refs
  const addressLine1Ref = useRef(null);
  const addressLine2Ref = useRef(null);

  const [{ configuration }, dispatch] = useContext(StateContext);

  function resetForm() {
    setAutocompleteComponentValue(null);
    setGooglePlaceRaw(initialPlaceState);
    setAddressSearchInputValue(initialState.street_address_1);
    setAddressLine2(initialState.street_address_2);
    setCity(initialState.city);
    setPostalCode(initialState.postal_code);
    setStateDropdownValue(null);
    setLimitToGoogleResults(restrictToGoogleResults);
    setGooglePlacesOptionsForDropdown([]);
    addressLine1Ref.current.focus();

    // update session storage
    if (enableBrowserCache) {
      window.sessionStorage.removeItem(browserCacheKey);
    }
  }

  // Load Google Maps script
  useEffect(() => {
    if (!googleApiKey) {
      // show error modal if googleApiKey is not set
      dispatch(
        actions.modals.showUiErrorModal(configuration, {
          errorType: ERROR_TYPES.UI_ERRORS.MISSING_API_KEY,
          contentProps: {
            showFaqLink: false,
            description:
              'Missing Google Maps API key. Please contact payment administrator for support, or choose another payment network.',
            actions: [
              {
                label: 'Start Over',
                onClick: () => dispatch(actions.startOver()),
              },
            ],
          },
        })
      );
      return;
    }

    // load google maps api script when component mounts
    const unloadGooglePlacesScript = loadGooglePlacesScript(
      `https://maps.googleapis.com/maps/api/js?key=${googleApiKey}&libraries=places`,
      onGooglePlacesScriptLoad
    );

    // remove script on unmount to prevent multiple scripts from being loaded on the page
    return () => unloadGooglePlacesScript();
  }, [googleApiKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // get predictions from google autocomplete service
  const getPredictions = React.useMemo(
    () =>
      throttle((request, callback) => {
        autocompleteService.current.getPlacePredictions(request, callback);
      }, autocompleteThrottleDurationInMs),
    [autocompleteThrottleDurationInMs]
  );

  // get full details for a selected place
  const getPlaceDetails = React.useMemo(
    () => (request, callback) => {
      placesService.current.getDetails(request, callback);
    },
    []
  );

  // if a google place object is selected from the google results dropdown, receive the place details and extract the address fields
  const onIncomingPlaceDetails = (place, status) => {
    if (status !== 'OK') {
      resetForm();

      // reset session storage
      if (enableBrowserCache) {
        window.sessionStorage.removeItem(browserCacheKey);
      }
      return;
    }

    const formattedPlace = parsePlace(place);

    const {
      route,
      street_number,
      administrative_area_level_1,
      premise,
      postal_code,
      locality,
    } = formattedPlace;

    let street_address_1 = '';
    if (street_number && route) {
      street_address_1 = `${street_number} ${route}`;
    } else if (premise) {
      street_address_1 = premise;
    }
    formattedPlace.street_address_1 = street_address_1;

    if (!street_address_1) {
      logger({
        event: LOGGING.EVENTS.UNEXPECTED_AUTOCOMPLETE_RESPONSE,
        inputValue: addressSearchInputValue,
        formattedPlace,
        rawPlace: place,
      });
    }

    setGooglePlaceRaw(formattedPlace);
    setAddressSearchInputValue(street_address_1);
    setStateDropdownValue(
      mapStateAbbreviationToName[administrative_area_level_1]
    );
    setCity(locality);
    setPostalCode(postal_code);
  };

  function getAutocompletePredictions() {
    // ignore if autocomplete service is not loaded
    if (!autocompleteService.current) {
      return undefined;
    }

    // reset dropdown and current place if input is empty
    if (addressSearchInputValue === '') {
      setGooglePlacesOptionsForDropdown([]);

      // update session storage
      if (enableBrowserCache) {
        window.sessionStorage.removeItem(browserCacheKey);
      }

      setStateDropdownValue(null);
      if (error) setError(false);
      return undefined;
    }

    // get new predictions from google autocomplete service
    getPredictions(
      {
        input: addressSearchInputValue,
        sessionToken: googleSessionToken,
        componentRestrictions: { country: 'us' },
      },
      (results) => {
        let newOptions = [];

        if (results) {
          // filter for only complete addresses (no cities or neighborhoods, businesses, intersections, etc.)
          newOptions = results.filter(
            (result) =>
              result.types.includes('street_address') ||
              result.types.includes('premise') ||
              result.types.includes('subpremise')
          );
        }

        // if no options are returned and we are restricting to google results, show error
        if (!newOptions.length) {
          if (limitToGoogleResults) {
            setError(true);
          }
        } else {
          // clear any previous error if options are returned
          if (error) setError(false);
        }

        // if limitToGoogleResults is true and allowManualOverride is true, add a custom option to the dropdown to allow the user to enter the address manually
        if (!newOptions.length && limitToGoogleResults && allowManualOverride) {
          newOptions.push({ id: 'address-not-listed' });
        } else if (
          newOptions.length &&
          limitToGoogleResults &&
          allowManualOverride
        ) {
          // if results are returned but the options don't match the user's expectations, allows them to enter the address manually
          newOptions.push({ id: 'allow-manual-entry' });
        }

        // show google attribution if there are any options
        if (newOptions.length && newOptions[0].id !== 'address-not-listed') {
          newOptions.push({ id: 'google-attribution' });
        }

        setGooglePlacesOptionsForDropdown(newOptions);
      }
    );
  }

  // any time the address search input value changes, get predictions from google autocomplete service
  useEffect(
    () => {
      getAutocompletePredictions();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addressSearchInputValue]
  );

  // if a google place object is selected from the google results dropdown, get the place details
  useEffect(
    () => {
      if (!placesService.current) {
        return undefined;
      }

      // if a google place object is selected from the google results dropdown, get the place details
      if (autocompleteComponentValue && autocompleteComponentValue.place_id) {
        getPlaceDetails(
          {
            placeId: autocompleteComponentValue.place_id,
            fields: ['address_components', 'formatted_address'],
          },
          onIncomingPlaceDetails
        );

        googleSessionToken =
          new window.google.maps.places.AutocompleteSessionToken();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [autocompleteComponentValue]
  );

  // anytime a form field is changed, update the state, validate the form, and sync the changes to the parent component
  useEffect(
    () => {
      const newState = {
        street_address_1: addressSearchInputValue,
        street_address_2: addressLine2,
        city,
        state: mapStateNameToAbbreviation[stateDropdownValue],
        postal_code: postalCode,
        google_place_raw: googlePlaceRaw,
      };

      const manual_edit =
        !googlePlaceRaw || // if user clears the autocomplete dropdown, googlePlaceRaw will be null
        newState.street_address_1 !== googlePlaceRaw.street_address_1 ||
        newState.city !== googlePlaceRaw.locality ||
        newState.state !== googlePlaceRaw.administrative_area_level_1 ||
        newState.postal_code !== googlePlaceRaw.postal_code;

      onChange({
        ...newState,
        google_place_raw: googlePlaceRaw,
        manual_edit,
      });

      // update session storage
      if (enableBrowserCache) {
        window.sessionStorage.setItem(
          browserCacheKey,
          JSON.stringify(newState)
        );
      }

      const isValid = Boolean(
        newState.street_address_1 &&
          newState.city &&
          newState.state &&
          newState.postal_code &&
          !error &&
          !(
            limitToGoogleResults &&
            newState.street_address_1 !== googlePlaceRaw?.street_address_1
          )
      );

      onValidationChange(isValid);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      addressSearchInputValue,
      addressLine2,
      city,
      stateDropdownValue,
      postalCode,
      googlePlaceRaw,
      error,
    ]
  );

  return (
    <>
      <div className={classes.root}>
        <Autocomplete
          className={classes.input}
          classes={{
            option: classes.opaque,
            noOptions: clsx({ [classes.hidden]: !addressSearchInputValue }),
          }}
          getOptionLabel={(option) => {
            // option is a string while user is typing, and a google place object when a google result is selected
            try {
              return typeof option === 'string'
                ? option
                : option.structured_formatting.main_text;
            } catch (error) {
              return addressSearchInputValue;
            }
          }}
          getOptionDisabled={(option) =>
            option.disabled || option.id === 'google-attribution'
          }
          noOptionsText={
            limitToGoogleResults && !allowManualOverride && error
              ? 'Your address could not be validated by Google'
              : 'No matches found'
          }
          filterOptions={(options, state) => options} // fixed a bug where the options were being filtered by material-ui even without a filtering function specified
          onFocus={() => {
            // if user returns to the input field, get predictions from google autocomplete service, instead of showing no options
            if (addressSearchInputValue) {
              getAutocompletePredictions();
            }
          }}
          disableClearable={false}
          onClose={() => {
            if (error) {
              if (limitToGoogleResults) {
                setAutocompleteComponentValue(null);
                setGooglePlaceRaw(null);
                setAddressSearchInputValue(initialState.street_address_1);
                setAddressLine2(initialState.street_address_2);
                setCity(initialState.city);
                setPostalCode(initialState.postal_code);
                setStateDropdownValue(null);
              }
              setError(false);
            }
          }}
          getOptionSelected={(option, value) =>
            option.place_id === value.place_id
          }
          options={googlePlacesOptionsForDropdown}
          autoComplete
          debug={debug}
          autoHighlight={limitToGoogleResults} // auto highlight first option
          autoSelect={limitToGoogleResults} // If true, the selected option becomes the value of the input when the Autocomplete loses focus unless the user chooses a different option or changes the character string in the input.
          freeSolo={!limitToGoogleResults}
          includeInputInList
          clearOnEscape={limitToGoogleResults} // clear input and options on escape
          value={autocompleteComponentValue}
          onChange={(event, newPlace, reason) => {
            setAutocompleteComponentValue(newPlace);

            if (reason === 'select-option') {
              // if user has results available but doesn't see their address, allow them to edit it manually
              if (
                newPlace.id === 'allow-manual-entry' ||
                newPlace.id === 'address-not-listed'
              ) {
                setLimitToGoogleResults(false);
              } else {
                // if user selects a google result, restore the original value of restrictToGoogleResults (for instance, if they are manually editing the address, and then select another google result, it should be re-locked to google results)
                setLimitToGoogleResults(restrictToGoogleResults);
                addressLine2Ref.current.focus();
              }
            } else if (reason === 'clear') {
              resetForm();
            }
          }}
          onInputChange={(event, newInputValue) =>
            setAddressSearchInputValue(newInputValue)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                value: addressSearchInputValue,
                'data-lpignore': 'true',
                autoComplete: 'new-password',
              }}
              inputRef={addressLine1Ref}
              // autoFocus
              error={Boolean(error)}
              label="Address"
              variant="outlined"
              fullWidth
              size="small"
              required
            />
          )}
          renderOption={(option) => {
            if (option.id === 'address-not-listed') {
              return (
                <Grid container alignItems="center">
                  <Grid item>
                    <div style={{ fontSize: 14, lineHeight: '20px' }}>
                      Address not listed?{' '}
                      <div style={{ color: 'red' }}>
                        Click to enter it manually (manual edits are subject to
                        review, and may result in delayed processing times)
                      </div>
                    </div>
                  </Grid>
                </Grid>
              );
            }
            if (option.id === 'allow-manual-entry') {
              return (
                <Grid container alignItems="center">
                  <Grid item>
                    <div style={{ fontSize: 14, lineHeight: '20px' }}>
                      Don't see your address?{' '}
                      <div style={{ color: 'red' }}>
                        Click to enter it manually (manual edits are subject to
                        review, and may result in delayed processing times)
                      </div>
                    </div>
                  </Grid>
                </Grid>
              );
            } else if (option.id === 'google-attribution') {
              return (
                <Grid container alignItems="center">
                  <Grid item>
                    <img
                      alt="powered by Google"
                      src={poweredByGoogleLogo}
                      style={{ height: 15 }}
                    />
                  </Grid>
                </Grid>
              );
            }

            try {
              const matches =
                option.structured_formatting.main_text_matched_substrings;
              const parts = parse(
                option.structured_formatting.main_text,
                matches.map((match) => [
                  match.offset,
                  match.offset + match.length,
                ])
              );

              return (
                <Grid container alignItems="center">
                  <Grid item>
                    <LocationOnIcon className={classes.icon} />
                  </Grid>
                  <Grid item xs style={{ lineHeight: 1 }}>
                    {parts.map((part, index) => (
                      <span
                        className={classes.primary}
                        key={index}
                        style={{ fontWeight: part.highlight ? 700 : 400 }}
                      >
                        {part.text}
                      </span>
                    ))}

                    <Typography
                      color="textSecondary"
                      className={classes.secondary}
                      style={{
                        padding: 0,
                        textAlign: 'left',
                      }}
                    >
                      {option.structured_formatting.secondary_text}
                    </Typography>
                  </Grid>
                </Grid>
              );
            } catch (error) {
              // if error occurs it is because we are using a custom option, in the case that no results are returned
              return option;
            }
          }}
        />
        <TextField
          className={classes.input}
          inputProps={{
            'data-lpignore': 'true',
          }}
          inputRef={addressLine2Ref}
          value={addressLine2}
          variant="outlined"
          size="small"
          onChange={(evt) => setAddressLine2(evt.target.value)}
          label="Suite/Apt #"
        />
        <TextField
          className={clsx(classes.input, {
            [classes.disabled]: limitToGoogleResults,
          })}
          value={city}
          label="City"
          variant="outlined"
          size="small"
          inputProps={{
            'data-lpignore': 'true',
            autoComplete: 'new-password', // disable autocomplete and autofill
          }}
          required
          readOnly={limitToGoogleResults}
          onChange={
            limitToGoogleResults ? () => {} : (evt) => setCity(evt.target.value)
          }
        />
        <div
          style={{
            display: 'flex',
            marginTop: 20,
            justifyContent: 'space-between',
            width: 300,
          }}
        >
          <Autocomplete
            options={stateNames}
            className={clsx(classes.input, classes.inline, {
              [classes.disabled]: limitToGoogleResults,
            })}
            autoSelect
            value={stateDropdownValue}
            disableClearable
            onChange={(event, stateName) => setStateDropdownValue(stateName)}
            autoHighlight
            readOnly={limitToGoogleResults}
            debug={debug}
            renderInput={(params) => (
              <TextField
                {...params}
                style={{ width: 175 }}
                required
                label="State"
                variant="outlined"
                size="small"
                inputProps={{
                  ...params.inputProps,
                  'data-lpignore': 'true',
                  autoComplete: 'new-password', // disable autocomplete and autofill
                }}
              />
            )}
          />
          <TextField
            className={clsx(classes.input, {
              [classes.disabled]: limitToGoogleResults,
            })}
            InputLabelProps={{
              classes: {
                root: classes.inputLabel,
                outlined: 'outlined',
                marginDense: 'marginDense',
                shrink: 'shrink',
              },
            }}
            style={{ width: 120, marginTop: 0 }}
            value={postalCode}
            label="Postal Code"
            variant="outlined"
            size="small"
            required
            inputProps={{
              'data-lpignore': 'true',
              style: { padding: '10.5px 10px', textAlign: 'center' },
            }}
            error={Boolean(
              postalCode && !/(^\d{5}$)|(^\d{5}-\d{4}$)/.test(postalCode)
            )}
            readOnly={limitToGoogleResults}
            onChange={(evt) => setPostalCode(evt.target.value)}
          />
        </div>
      </div>
    </>
  );
}
